import React from 'react';

import { useSelector } from 'react-redux';

import clsx from 'clsx';
import _get from 'lodash/get';

import { useDevice } from '@bonnet/next/device';

import { pageNames } from '@atc/bonnet-paths';
import { useBrand } from '@atc/brand';
import { useFeatures } from '@atc/features';

import { srpAdsDuck } from '@/ducks/srp';

import AtcAdSlot from '@/containers/AtcAdSlot';
import DisableAdsFeatureFlag from '@/containers/global/DisableAdsFeatureFlag';

const { SEARCH_RESULTS } = pageNames;

const desktopBackFillSize = [728, 90];
const mobileBackFillSize = [300, 250];

// ensure ad sizes are in order from shortest to tallest to make sure min size is smallest height
const desktopSizes = [
    desktopBackFillSize,
    [728, 200],
    'fluid',
];

const kbbMobileSizes = [
    [320, 50],
    mobileBackFillSize,
    [306, 435],
    'fluid',
];

const atcMobileSizes = [
    mobileBackFillSize,
    [306, 435],
    'fluid',
];

//  default props by listing index
const defaultAtcAdProps = {
    0: { id: 'spotlightAd-toptier', position: 'b', sizes: desktopSizes, xsSizes: atcMobileSizes },
    1: { id: 'ad_srp_lstg_1', position: 'c', sizes: desktopSizes, xsSizes: atcMobileSizes },
    2: { id: 'ad_srp_lstg_2', position: 'd', sizes: desktopSizes, xsSizes: atcMobileSizes },
    3: { id: 'ad_srp_lstg_5', position: 'j', sizes: desktopSizes, xsSizes: atcMobileSizes },
    4: { id: 'ad_srp_lstg_6', position: 'k', sizes: desktopSizes, xsSizes: atcMobileSizes },
};

const fluidAdAtcAdProps = {
    0: { id: 'spotlightAd-toptier', position: 'b', sizes: [[300, 250], [728, 200], 'fluid'], xsSizes: [...atcMobileSizes, [1, 505]] },
    1: { id: 'ad_srp_lstg_1', position: 'c', sizes: [[300, 250], [728, 200], 'fluid'], xsSizes: [...atcMobileSizes, [1, 505]] },
    2: { id: 'ad_srp_lstg_2', position: 'd', sizes: [[300, 250], [728, 200], 'fluid'], xsSizes: [...atcMobileSizes, [1, 505]] },
    3: { id: 'ad_srp_lstg_5', position: 'j', sizes: [[300, 250], [728, 200], 'fluid'], xsSizes: [...atcMobileSizes, [1, 505]] },
    4: { id: 'ad_srp_lstg_6', position: 'k', sizes: [[300, 250], [728, 200], 'fluid'], xsSizes: [...atcMobileSizes, [1, 505]] },
};

const desktopKbbAdProps = {
    0: { id: 'kbbAdsClassfiedsInlineOEM', position: '29', sizes: desktopSizes },
    1: { id: 'kbbAdsClassfiedsInlineOEM2', position: '29', sizes: desktopSizes },
    2: { id: 'kbbAdsClassfiedsInlineOEM3', position: '29', sizes: desktopSizes },
    3: { id: 'kbbAdsClassfiedsInlineOEM4', position: '29', sizes: desktopSizes },
    4: { id: 'kbbAdsClassfiedsInlineOEM5', position: '29', sizes: desktopSizes },
};

const mobileKbbAdProps = {
    0: { id: 'kbbAdsMmaBanner1', position: 'M1', xsSizes: kbbMobileSizes },
    1: { id: 'kbbAdsMmaBanner2', position: 'M1', xsSizes: kbbMobileSizes },
    2: { id: 'kbbAdsMmaBanner3', position: 'M1', xsSizes: kbbMobileSizes },
    3: { id: 'kbbAdsMmaBanner4', position: 'M1', xsSizes: kbbMobileSizes },
    4: { id: 'kbbAdsMmaBanner5', position: 'M1', xsSizes: kbbMobileSizes },
};

function ListingsLinerAds({
    index,
}) {
    const {
        fluid_ad: [enabledFluidAd],
    } = useFeatures(['fluid_ad']);

    const device = useDevice();
    const notXs = _get(device, 'greaterThan.xs', false);

    const { brand, brands } = useBrand();
    const { KBB_BRAND } = brands;

    const adsLazyLoadingOffset = useSelector(srpAdsDuck.selectors.getAdsLazyLoadingOffset);

    let adProps = enabledFluidAd ? fluidAdAtcAdProps[index] : defaultAtcAdProps[index];

    if (KBB_BRAND === brand) {
        adProps = notXs ? desktopKbbAdProps[index] : mobileKbbAdProps[index];
    }

    if (!adProps) {
        return null;
    }

    const sizes = notXs ? adProps.sizes : adProps.xsSizes;
    const adTargeting = {
        pos: adProps.position,
        prd: 'rc12',
    };

    return (
        <DisableAdsFeatureFlag>
            <AtcAdSlot
                className={clsx('display-flex flex-column', {
                    'justify-content-center': !enabledFluidAd,
                })}
                lazyLoading
                lazyLoadingBottomOffset={adsLazyLoadingOffset}
                lazyLoadingTopOffset={adsLazyLoadingOffset}
                // preserve space on mobile even if you are in the fluid ad test.  We should always preserve space for the max ad
                // size, but temporarily we are removing that constraint on desktop because the ads in the test did not account for that.
                preserveSpace={!notXs || !enabledFluidAd}
                showPlaceholder
                size={sizes}
                slotId={adProps.id}
                targeting={adTargeting}
                adSelector={srpAdsDuck.selectors.getDuckState}
                pageName={SEARCH_RESULTS}
            />
        </DisableAdsFeatureFlag>
    );
}

export default ListingsLinerAds;
